import React, { useRef } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import {
  Box,
  Button,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material'
import _ from 'lodash'
import getSubmissionErrors from '../../utils/submissionErrors'
import { useNavigate } from 'react-router-dom'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'

import { states } from '../../constants/states'
import dealershipBrandsDict, { dealershipBrandOptions } from '../../constants/dealershipBrands'
import { useQueryClient } from 'react-query'
import { AutocompleteElement, TextFieldElement } from 'react-hook-form-mui'
import { useCreateDealership, useEditDealership } from '../../hooks/api/dealerships'

const DealershipForm = ({ dealership, refetch }) => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const { mutateAsync: updateDealership } = useEditDealership()
  const { mutateAsync: createDealership } = useCreateDealership()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    criteriaMode: 'all',
    defaultValues: {
      brand: dealership?.brand,
      name: dealership?.name,
      state: dealership?.state,
      registration: dealership?.registration,
      registrationLegacy: dealership?.registrationLegacy,
      address: dealership?.address,
      phone: dealership?.phone || [''],
      fax: dealership?.fax || [''],
    },
  })

  const fieldArrays = {
    phone: useFieldArray({ control, name: 'phone' }),
    fax: useFieldArray({ control, name: 'fax' }),
  }

  const onSubmit = async (data) => {
    try {
      if (dealership) {
        await updateDealership({
          dealershipId: dealership._id,
          ..._.pickBy(data, _.identity),
        })
        refetch()
      } else {
        await createDealership(data)
      }
      queryClient.invalidateQueries(['dealerships'])
      navigate('/admin/dealerships')
    } catch (err) {
      for (const { name, message } of getSubmissionErrors(err)) {
        setError(name, { message })
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        {dealership?._id && (
          <Box>
            <Typography component='span' variant='body1'>
              Dealership ID:{' '}
            </Typography>
            <Typography component='span' variant='body2'>
              {dealership._id}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AutocompleteElement
              control={control}
              name='brand'
              label='Brand'
              rules={{
                required: 'Brand is required.',
              }}
              matchId
              textFieldProps={{
                placeholder: dealership?.brand
                  ? dealershipBrandsDict[dealership?.brand] || dealership?.brand
                  : undefined,
                InputLabelProps: { shrink: Boolean(dealership?.brand) || undefined },
              }}
              required
              options={dealershipBrandOptions}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldElement
              control={control}
              name='name'
              label='Name'
              validation={{
                required: 'Dealership name is required.',
              }}
              fullWidth
              required
              placeholder={dealership?.name}
              InputLabelProps={{ shrink: Boolean(dealership?.name) || undefined }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              control={control}
              name='registration'
              label='Company Registration Number (New Format)'
              fullWidth
              placeholder={dealership?.registration}
              InputLabelProps={{ shrink: Boolean(dealership?.registration) || undefined }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              control={control}
              name='registrationLegacy'
              label='Company Registration Number (Old Format)'
              fullWidth
              placeholder={dealership?.registrationLegacy}
              InputLabelProps={{ shrink: Boolean(dealership?.registrationLegacy) || undefined }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldElement
              control={control}
              multiline
              minRows={3}
              name='address'
              label='Address'
              fullWidth
              placeholder={dealership?.address}
              InputLabelProps={{ shrink: Boolean(dealership?.address) || undefined }}
            />
          </Grid>
          <Grid item xs={12}>
            <AutocompleteElement
              control={control}
              name='state'
              label='State'
              options={states}
              autocompleteProps={{
                isOptionEqualToValue: (option, value) => option === value,
                freeSolo: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormLabel>Telephone Numbers</FormLabel>
              {fieldArrays.phone.fields.map((item, index) => (
                <TextFieldElement
                  control={control}
                  key={item.id}
                  fullWidth
                  name={`phone.${index}`}
                  type='tel'
                  inputProps={{
                    inputMode: 'tel',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => fieldArrays.phone.remove(index)}
                          aria-label='Remove'
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}
              <Button
                type='button'
                startIcon={<AddIcon />}
                onClick={() => {
                  fieldArrays.phone.append('')
                }}
              >
                Add Telephone Number
              </Button>
            </FormGroup>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormLabel>Fax Numbers</FormLabel>
              {fieldArrays.fax.fields.map((item, index) => (
                <TextFieldElement
                  control={control}
                  key={item.id}
                  fullWidth
                  name={`fax.${index}`}
                  type='tel'
                  inputProps={{
                    inputMode: 'tel',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => fieldArrays.fax.remove(index)}
                          aria-label='Remove'
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}
              <Button
                type='button'
                startIcon={<AddIcon />}
                onClick={() => {
                  fieldArrays.fax.append('')
                }}
              >
                Add Fax Number
              </Button>
            </FormGroup>
          </Grid>
        </Grid>

        <Box gap={2} display='flex' justifyContent='flex-end'>
          <Button
            type='button'
            onClick={() => {
              reset()
            }}
            disabled={isSubmitting}
          >
            Reset
          </Button>
          <Button type='submit' variant='contained' disabled={isSubmitting}>
            Submit
          </Button>
        </Box>
      </Box>
    </form>
  )
}

export default DealershipForm
